import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of, switchMap, tap, throwError} from 'rxjs';
import {AuthUtils} from 'app/core/auth/auth.utils';
import {UserService} from 'app/core/user/user.service';
import {User} from '../user/user.types';
import {Api} from "@core/models/Api.model";
import {ResultResponseInterface} from "@shared/interfaces/result-response.interface";
import {MsalService} from '@azure/msal-angular';
import {AuthenticationResult} from '@azure/msal-browser';
import {PermitService} from 'app/shared/services/permitService/permitService.service';


@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    private user: User;

    /**
     * Constructor
     */
    constructor(
        private readonly _httpClient: HttpClient,
        private readonly _userService: UserService,
        private readonly _msalService: MsalService,
        private readonly _permitService: PermitService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    /**
     * Setter & getter for access token
     */
    set permissionsToken(token: string) {
        localStorage.setItem('permissionsToken', token);
    }

    get permissionsToken(): string {
        return localStorage.getItem('permissionsToken') ?? '';
    }

    /**
     * Setter & getter for refresh token
     */
    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    private storeJwtTokens(tokens: { refresh: string; access: string; permissions: string; }): void {
        this.accessToken = tokens.access;
        this.permissionsToken = tokens.permissions;
        this.refreshToken = tokens.refresh;        
    }

    removeTokens(): void {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('permissionsToken');
        localStorage.removeItem('banner');
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { username: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient.post<ResultResponseInterface<{
            refresh: string;
            access: string;
            permissions: string;
        }>>(`${Api.urlLogin}`, credentials).pipe(
            map(resp => resp.objeto),
            switchMap((response) => {

                // Store the access token in the local storage
                this.storeJwtTokens(response)

                // Refresh permissions
                this._permitService.refreshPermissions();

                // Set the authenticated flag to true
                this._authenticated = true;

                this.user = {
                    id: '',
                    name: credentials.username,
                    email: '',
                    status: 'online',
                }

                // Store the user on the user service
                this._userService.user = this.user;
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // El catchError de arriba resuelve a `false` — respetarlo. Sin
                // esto, un token rechazado terminaba marcando autenticado.
                if (response === false) {
                    return of(false);
                }

                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                    //VERIFICAR ESTA OPCION PORQUE DEBERIA TRAERSE PERSMISOS Y REFRESH
                    //this.permissionsToken = response.permissions;
                    //this.refreshToken = response.refresh_token;
                }

                // Refresh permissions
                this._permitService.refreshPermissions();

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Auto Sign in
     *
     * @param token
     */
    autoSignIn(autToken: string): Observable<any> {  
        const url = `${Api.url_auto_ingreso}/${autToken}`;     
        return this._httpClient.get<ResultResponseInterface<{
            refresh: string;
            access: string;
            permissions: string;
            email: string;
        }>>(url).pipe(
            map(resp => resp.objeto),
            switchMap((response) => {
                if(!AuthUtils.isTokenExpired(response.access)){
                    this.user = {
                        id: '',
                        name: '',
                        email: response.email,
                        status: 'online',
                    }
                    this.storeJwtTokens(response);
                    this._permitService.refreshPermissions();
                    this._userService.user = this.user;
                    return of(response);
                }
                
                return of(response);
            })
        );
    }

    /**
     * Sign in with Microsoft
     * Inicia el flujo de autenticación de Microsoft y envía el token al backend
     */
    signInWithMicrosoft(): Observable<any> {
        return new Observable(observer => {
            // Asegurarse de que MSAL esté inicializado
            this._msalService.instance.initialize().then(() => {
                this._msalService.loginPopup({
                    scopes: ['user.read', 'openid', 'profile', 'email']
                }).subscribe({
                next: (result: AuthenticationResult) => {
                    // Token de acceso de Microsoft
                    const microsoftToken = result.accessToken;
                    const idToken = result.idToken;
                    
                    // Enviar el token al backend para validación y obtener JWT interno
                    this._httpClient.post<ResultResponseInterface<{
                        refresh: string;
                        access: string;
                        permissions: string;
                        email: string;
                        name: string;
                    }>>(`${Api.BaseUrlApp}auth/microsoft/`, {
                        access_token: microsoftToken,
                        id_token: idToken
                    }).pipe(
                        map(resp => resp.objeto),
                        switchMap((response) => {
                            // Almacenar los tokens JWT internos
                            this.storeJwtTokens(response);
                            
                            // Refresh permissions
                            this._permitService.refreshPermissions();

                            // Marcar como autenticado
                            this._authenticated = true;
                            
                            // Configurar el usuario
                            this.user = {
                                id: '',
                                name: response.name || result.account?.name || '',
                                email: response.email || result.account?.username || '',
                                status: 'online',
                            };
                            
                            // Guardar en el servicio de usuario
                            this._userService.user = this.user;
                            
                            return of(response);
                        })
                    ).subscribe({
                        next: (response) => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error) => {
                            observer.error(error);
                        }
                    });
                },
                error: (error) => {
                    console.error('Error en login de Microsoft:', error);
                    observer.error(error);
                }
            });
            }).catch(error => {
                console.error('Error al inicializar MSAL:', error);
                observer.error(error);
            });
        });
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        this.removeTokens();

        // Clear permissions
        this._permitService.refreshPermissions();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Si el usuario está autenticado con Microsoft, cerrar sesión también allí.
        // OJO: getAllAccounts() LANZA si MSAL aún no se inicializó (carga fresca:
        // el init corre async en AppComponent) — sin el try/catch, la excepción
        // dentro del catch del interceptor se saltaba el redirect a /sign-in.
        try {
            const accounts = this._msalService.instance.getAllAccounts();
            if (accounts.length > 0) {
                this._msalService.logoutPopup({
                    account: accounts[0]
                }).subscribe({ error: () => { /* popup bloqueado: ignorar */ } });
            }
        } catch (e) {
            console.warn('[Auth] MSAL no inicializado al cerrar sesión, se omite logout de Microsoft.');
        }

        // Return the observable
        return of(true);
    }

    /**
     * Sign out silencioso (sin popup de Microsoft) - usado para cierre por inactividad
     */
    signOutSilent(): Observable<any> {
        // Remove the access token from the local storage
        this.removeTokens();

        // Clear permissions
        this._permitService.refreshPermissions();

        // Set the authenticated flag to false
        this._authenticated = false;

        // No intentar cerrar sesión de Microsoft para evitar popups
        console.log('[Auth] Cierre de sesión silencioso completado');

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(data: { name: string; last_name: string; user: string; email: string; password: string }): Observable<any> {
        return this._httpClient.post(`${Api.BaseUrlApp}create-user/`, data);
    }


    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Token vencido (o ilegible) → login limpio. Antes esto pasaba por el
        // MOCK de Fuse ('api/auth/sign-in-with-token'), que rechaza los JWT
        // reales con 401, y el switchMap igual marcaba autenticado → el
        // usuario entraba a home con credenciales muertas y rebotaba.
        let vencido = true;
        try {
            vencido = AuthUtils.isTokenExpired(this.accessToken);
        } catch {
            vencido = true;
        }
        if (vencido) {
            return of(false);
        }

        // Token vigente: restaurar la sesión localmente, sin round-trip al mock
        this._authenticated = true;
        this._permitService.refreshPermissions();
        return of(true);
    }

    /**
     * Solicitud de token de actualización
     */
    public refreshTokenRequest(): Observable<any> {
        return this._httpClient.post(`/auth/token`, {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken
        }, {
            headers: {refresh_token: this.refreshToken || ''}
        }).pipe(
            tap(resp => {
                this.storeJwtTokens(resp)
                this._permitService.refreshPermissions();
            })
        );
    }
    
    public refreshTokenRequestNew(): Observable<any> {
        return this._httpClient.post(Api.refreshPermissions, {
            refresh: this.refreshToken
        }).pipe(
            tap(resp => {
                resp = resp.objeto
                resp['refresh'] = this.refreshToken
                this.storeJwtTokens(resp)
                this._permitService.refreshPermissions();
            })
        );
    }

}
